/**
 * Wire types for the Admin API (`/admin/v1`, apps/api/admin) and the Observability
 * API (`/ops/v1`, apps/api/observability). The Rust route handlers are the source
 * of truth; each block below names the handler it mirrors.
 *
 * Conventions:
 * - snake_case field names, RFC 3339 UTC timestamps.
 * - IDs are UUID strings (UUIDv7 for most records); the UI treats them as opaque.
 * - Many admin reads return `to_jsonb(row)`: every column of the table. Only the
 *   columns the UI uses are typed; the rest are ignored.
 * - Failures use the envelope `{ error: { code, message, request_id, details } }`
 *   (see `ApiError` in client.ts).
 */

import type { AdminApi } from './areas/admin';
import type { AccountsApi, SubjectProfile } from './areas/accounts';
import type { NotifyApi } from './areas/notify';
import type { WebhooksApi } from './areas/webhooks';
import type { ModulesApi } from './areas/modules';
import type { AddonsApi } from './areas/addons';
import type { ZnsApi } from './areas/zns';
import type { CatalogApi } from './areas/catalog';
import type { SubscriptionsApi } from './areas/subscriptions';
import type { EinvoiceApi } from './areas/einvoice';
import type { ReportingApi } from './areas/reporting';
import type { NameChangeApi } from './areas/namechange';
import type { ServicesApi } from './areas/services';
import type { CountsApi } from './areas/counts';
import type { HostingApi } from './areas/hosting';
import type { DnsApi } from './areas/dns';
import type { WorkApi } from './areas/work';
import type { DeclarationsApi } from './areas/declarations';
import type { CommerceApi } from './areas/commerce';
import type { CoreApi } from './areas/core';
import type { GovernanceApi } from './areas/governance';
import type { IntegrationsApi } from './areas/integrations';
import type { LookupApi } from './areas/lookup';
import type { RegistryApi } from './areas/registry';
import type { MarketApi } from './areas/market';
import type { OperatorApi } from './areas/operator';

export type ISODateTime = string;
/** Exact decimal, e.g. "150000", "-12.50". Never a JSON number. */
export type DecimalString = string;

export interface Money {
  amount: DecimalString;
  currency: string;
}

/** Non-paged list envelope (`{ "items": [...] }`) used by most admin and ops lists. */
export interface ItemList<T> {
  items: T[];
}

// ---------------------------------------------------------------------------
// Staff session (routes/session.rs)

export interface LoginRequest {
  email: string;
  password: string;
  /** Six-digit TOTP code. Required when the account has MFA enforced. */
  totp?: string;
}

export interface LoginResponse {
  token: string;
  /** True only when a valid TOTP code was supplied; sensitive actions need it. */
  mfa_verified: boolean;
  expires_in_seconds: number;
}

/** `GET /auth/demo-login`: the staff account of the OT&E quick sign-in (404 when it is off). */
export interface DemoLogin {
  email: string;
}

export interface StaffGrant {
  role: string;
  /** Rust `Debug` rendering of the grant scope, e.g. `Global`. */
  scope: string;
  permissions: string[];
}

export interface Me {
  user_id: string;
  mfa_verified: boolean;
  grants: StaffGrant[];
  deployment: Deployment;
}

/** The server this Control Center runs on (ADR-018: one environment per deployment). From
 * `GET /deployment` (no session needed, for the sign-in page) and `/me`. */
export interface Deployment {
  environment: 'ote' | 'production';
  /** True on the OT&E (test) server: every page shows the "test environment (OT&E)" banner. */
  test_system: boolean;
}

// ---------------------------------------------------------------------------
// Domains (routes/domains.rs)

export type LifecycleState =
  | 'DRAFT'
  | 'AWAITING_DOSSIER'
  | 'AWAITING_PAYMENT'
  | 'PENDING_CREATE'
  | 'CREATE_FAILED'
  | 'ACTIVE'
  | 'PENDING_RENEW'
  | 'PENDING_TRANSFER'
  | 'EXPIRED'
  | 'GRACE_PERIOD'
  | 'REDEMPTION'
  | 'PENDING_DELETE'
  | 'DELETED'
  | 'TRANSFERRED_OUT';

export type ComplianceState = 'UNKNOWN' | 'COMPLIANT' | 'ACTION_REQUIRED' | 'BLOCKED' | 'SUSPENDED';
export type DomainBillingState = 'NONE' | 'AWAITING_PAYMENT' | 'RESERVED' | 'PAID' | 'REFUND_PENDING' | 'REFUNDED' | 'OVERDUE';
export type ServiceState = 'NORMAL' | 'DNS_SUSPENDED' | 'SUSPENDED' | 'LOCKED';

/** Dossier states per master spec §15.3. `domain.dossier_state` is free text, so unknown values may appear. */
export type DossierStatus =
  | 'NOT_CREATED'
  | 'COLLECTING'
  | 'PENDING_IDENTITY'
  | 'PENDING_DOCUMENT'
  | 'PENDING_SIGNATURE'
  | 'PENDING_REVIEW'
  | 'COMPLETE'
  | 'INVALID'
  | 'EXPIRED'
  | 'CORRECTION_REQUIRED'
  | 'SUPERSEDED';

/** Mirrors `registry_core::state::CommandState` (SCREAMING_SNAKE_CASE on the wire). */
export type RegistryCommandState =
  | 'PENDING'
  | 'SENT'
  | 'SUCCESS'
  | 'REJECTED'
  | 'UNKNOWN'
  | 'RECONCILING'
  | 'RETRY_PENDING'
  | 'MANUAL_REVIEW';

/** `GET /domains` row. */
export interface DomainSummary {
  id: string;
  /** Canonical key: lower-case A-label. */
  ascii_name: string;
  unicode_name: string;
  lifecycle_state: LifecycleState;
  dossier_state: string;
  compliance_state: ComplianceState;
  /** Expiry as recorded by the registry. */
  registry_expires_at: ISODateTime | null;
  /** What the customer has paid through; differs from the registry expiry by design. */
  customer_expires_at: ISODateTime | null;
  customer_id: string;
  customer_name: string;
  /** When the domain was added to the platform (the list is newest first). */
  created_at?: ISODateTime;
  /** Registry code, e.g. `VNNIC`. */
  registry_code: string;
  /** Registration date at the registry. */
  registry_created_at?: ISODateTime | null;
  auto_renew?: boolean;
  current_subject_id?: string | null;
  /** Legal name of the current subject's current version; null without `subject.read`. */
  subject_name?: string | null;
}

/** `sort` of `GET /domains`. */
export type DomainListSort =
  | 'created_at'
  | 'ascii_name'
  | 'registry_expires_at'
  | 'customer_expires_at'
  | 'registry_created_at'
  | 'customer_name'
  | 'lifecycle_state'
  | 'dossier_state'
  | 'compliance_state';

/** `GET /domains` filters. Dates are `YYYY-MM-DD` in Vietnam time, both ends included. */
export interface DomainListQuery {
  /** Prefix of the A-label or U-label. */
  q?: string;
  lifecycle_state?: readonly LifecycleState[];
  dossier_state?: readonly string[];
  compliance_state?: readonly ComplianceState[];
  customer_id?: string;
  /** Current registrant subject. */
  subject_id?: string;
  /** Registry code, e.g. `VNNIC`. */
  registry?: string;
  /** Name suffix, e.g. `vn`, `com.vn`. */
  tld?: string;
  auto_renew?: boolean;
  expires_from?: string;
  expires_to?: string;
  /** Which expiry `expires_from`/`expires_to` read (default the registry's). */
  expiry_basis?: 'registry' | 'customer';
  registered_from?: string;
  registered_to?: string;
  added_from?: string;
  added_to?: string;
  /** Paid-until (else registry) expiry between now and now + N days (1..3650). */
  expires_within_days?: number;
  /** Paid-until (else registry) expiry before this instant (past included). */
  expires_before?: string;
  sort?: DomainListSort;
  dir?: 'asc' | 'desc';
  /** 1-based. */
  page?: number;
  limit?: number;
}

export interface DomainListResponse {
  items: DomainSummary[];
  /** Rows matching the filters, all pages. */
  total: number;
  page: number;
  limit: number;
}

/** `to_jsonb(domain.domains)` plus the registry code, TLD zone and TLD class. */
export interface DomainRecord {
  id: string;
  ascii_name: string;
  unicode_name: string;
  tld_id: string;
  registry_id: string;
  customer_id: string;
  current_subject_id: string | null;
  lifecycle_state: LifecycleState;
  lifecycle_changed_at?: ISODateTime;
  compliance_state: ComplianceState;
  dossier_state: string;
  billing_state: DomainBillingState;
  transfer_state: string;
  dns_state: string;
  service_state: ServiceState;
  registry_created_at: ISODateTime | null;
  registry_expires_at: ISODateTime | null;
  customer_created_at: ISODateTime | null;
  customer_expires_at: ISODateTime | null;
  auto_renew: boolean;
  auto_renew_period: number;
  auth_info_set_at?: ISODateTime | null;
  version: number;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  /** Registry code, e.g. `VNNIC`. */
  registry: string;
  /** Registry name, e.g. `Vietnam Internet Network Information Center`. */
  registry_name: string;
  /** TLD zone, e.g. `vn`. */
  tld: string;
  tld_class: string;
  customer_name: string;
  customer_account_type: string;
  subject_type: string | null;
  /** Current legal name of the registrant subject; null without `subject.read`. */
  subject_display_name: string | null;
}

export interface DomainRegistryStatus {
  status: string;
  source: string;
  observed_at: ISODateTime;
}

export interface DomainLock {
  id: string;
  lock_type: string;
  reason: RecordedReason;
  case_reference: string | null;
  applied_at: ISODateTime;
  auto_release_at?: ISODateTime | null;
}

export interface DomainDossier {
  id: string;
  dossier_version: number;
  dossier_type: string;
  status: string;
  subject_id: string | null;
  subject_version_id: string | null;
  verification_id: string | null;
  policy_key: string | null;
  policy_version: string | null;
  completed_at: ISODateTime | null;
  /** Legal name of the pinned subject version; null without `subject.read`. */
  subject_display_name?: string | null;
  subject_version_number?: number | null;
  verification_method?: string | null;
  verification_status?: string | null;
  documents: {
    document_version_id: string;
    purpose: string;
    document_id?: string;
    document_type?: string;
    version_number?: number;
    bound_at?: ISODateTime;
    /** `DOSSIER`: the domain's own row (every document of a domain's dossier is its own). */
    owner_type?: string;
    /** Where the domain's copy was taken from (a subject's document, or its verification's evidence). */
    copied_from?: DocumentProvenance | null;
    /** The registrar-signed declaration of a valid confirmation: the dossier's current declaration. */
    registrar_signed?: boolean;
    /** The declaration a valid confirmation replaced, or a file replaced by its registrar-signed copy: kept (history), no longer sent. */
    superseded_by_registrar_signature?: boolean;
    /** An open request for the registrar's signature on this file ("Gửi NĐK ký"). */
    registrar_signature_request?: { item_id: string; status: string; origin: 'AUTO' | 'MANUAL'; queued_at: ISODateTime } | null;
  }[];
  /** What the dossier rests on (owner decisions 2026-10-01): EKYC, SUBJECT_DECLARATION, DOMAIN_DECLARATION (earlier), MANUAL. */
  verification_basis?: string | null;
  /** The VNNIC HSTM certification method (Phụ lục 1 Bảng 4): 1, 2, 4 or 5. */
  hstm_certify_method?: number | null;
  /** Where its domain declaration comes from. */
  declaration_source?: 'GENERATED_AFTER_EKYC' | 'GENERATED_FROM_SUBJECT_DECLARATION' | 'CUSTOMER_SIGNED' | 'PAPER_APPROVED' | null;
  /** The registrar's confirmations ("NĐK đã xác nhận bản khai"), the valid one first. */
  registrar_attestations?: RegistrarAttestation[];
  /** The verification as this dossier took it (method, result, evidence summaries). */
  verification_copy?: DossierVerificationCopy | null;
  /** `.vn`: the dossier's files as VNNIC's HSTM receives them. */
  vnnic_files?: DossierVnnicFiles | null;
}

/**
 * The registrar's confirmation of a dossier (ADR-024): the registrar checked it and digitally
 * signed its domain declaration — the signed file is the dossier's current declaration, the one
 * it replaced stays (history). VOID once the dossier changed (re-queued for signing).
 */
export interface RegistrarAttestation {
  id: string;
  status: 'VALID' | 'VOID';
  confirmed_at: ISODateTime;
  signed_document_version_id: string;
  replaced_document_version_id: string;
  certificate_serial: string;
  /** Null without `subject.read`. */
  certificate_subject: string | null;
  signing_service: string;
  precheck: unknown;
  voided_at: ISODateTime | null;
  void_reason: string | null;
}

/** Provenance of a document copied into a domain's dossier. */
export interface DocumentProvenance {
  owner_type: string;
  owner_id: string;
  document_version_id?: string;
  verification_id: string | null;
  copied_at: ISODateTime;
}

export interface DossierVerificationCopy {
  verification_id: string;
  subject_id: string;
  subject_version_id: string;
  method: string;
  status: string;
  provider: string | null;
  verified_at: ISODateTime | null;
  expires_at: ISODateTime | null;
  policy_key: string;
  policy_version: string;
  copied_at: ISODateTime;
  evidence: { evidence_type: string; summary: Record<string, unknown>; created_at: ISODateTime | null }[];
}

/** One file VNNIC HSTM-API v1.3 Appendix 2 requires: a type (or alternatives) and its signed status. */
export interface HstmRequiredFile {
  key: string;
  file_type: number | null;
  any_of: number[][] | null;
  /** 0 not signed, 1 signed by the registrant, 2 signed by the registrar. */
  signed_status: number;
  names: string[];
}

/** A file of the dossier sent to HSTM. */
export interface HstmPlannedFile {
  file_type: number;
  signed_status: number;
  stem: string;
  document_version_id: string;
  purpose: string;
  mime_type: string;
  size_bytes: number;
  sha256: string;
}

/** The HSTM plan of a `.vn` dossier (VN_HSTM_CATALOG_MAPPING): what VNNIC receives, what is missing. */
export interface DossierVnnicFiles {
  catalog: string;
  document: string;
  mapping_version: string;
  operation: string;
  document_type_id: number | null;
  document_type_name: string | null;
  /** HSTM certification method (1 account eKYC, 2 account digital signature, 4 domain digital signature). */
  certify_method: number | null;
  certify_method_name: string | null;
  /** Appendix / section and page the case comes from. */
  source: string | null;
  required: HstmRequiredFile[];
  files: HstmPlannedFile[];
  missing: { key: string; file_types: number[][]; signed_status: number; names: string[] }[];
  errors: string[];
  rejected: { document_version_id: string; purpose: string; file_type: number; problem: string }[];
  complete: boolean;
  /** Purposes staff can upload a file for (POST /dossier-queue/{id}/files). */
  slots: string[];
  /** Every file bound to the dossier with what was verified on it (absent from older servers). */
  bound_files?: HstmBoundFile[];
  /** Optional files sent without the registrar's signature where Phụ lục 2 asks for it (owner decision 2026-10-01: only the declaration is mandatory). */
  warnings?: string[];
  /** HSTM file types the upload waits for the registrar's signature on (data: today [5]). */
  registrar_signed_types?: number[];
  /** The HSTM verification information (documentCertify) the dossier would carry now. */
  verification?: HstmVerification | null;
}

/**
 * HSTM verification information (§2.5.2 documentCertify): methods 2 / 4 name the registrant's
 * signature (file 16 / file 5), methods 1 / 5 the registrar's on the domain declaration; method 1
 * adds the eKYC request id and score.
 */
export interface HstmVerification {
  method: 1 | 2 | 3 | 4 | 5 | number | null;
  method_label: string | null;
  ekyc_score: number | null;
  ekyc_request_id: string | null;
  /** The registrar's company, or the registrant's signer (null without `subject.read`). */
  signed_by: string | null;
  /** YYYY-MM-DD. */
  signed_at: string | null;
  /** Whose signature names the signer (present on the plan's verification only). */
  signed_by_party?: 'SUBJECT' | 'REGISTRAR';
}

/** A file bound to a `.vn` dossier: its signature as the platform verified it. */
export interface HstmBoundFile {
  document_version_id: string;
  purpose: string;
  document_type: string;
  mime_type: string;
  size_bytes: number;
  /** The registrant's digital signature on it (or on its source) was verified. */
  signed_by_subject: boolean;
  signed_by_registrar: boolean;
  /** A declaration naming this domain. */
  names_domain: boolean;
  /** The declaration generated for this domain's dossier. */
  generated_for_domain: boolean;
  /** Name on the signing certificate; null without `subject.read`. */
  signer: string | null;
  /** YYYY-MM-DD; null without `subject.read`. */
  signed_date: string | null;
  /** Replaced by the registrar-signed declaration: kept, not sent (ADR-024). */
  superseded?: boolean;
  /** The registrar's certificate name / signing date on a registrar-signed declaration. */
  registrar_signer?: string | null;
  registrar_signed_date?: string | null;
}

/** A registry refusal, one registrant field the registry would refuse (`REGISTRANT_INCOMPLETE`). */
export interface RegistrantProblem {
  /** `REGISTRANT`, `REPRESENTATIVE`, a contact role, or `DOMAIN`: the extra data the domain's TLD requires (field `extra.<key>`), completed on the domain, not on the subject. */
  contact: string;
  subject_id: string;
  field: string;
  label: string;
  message: string;
  rule: string;
}

/** A registration waiting for staff (`GET /domains/{key}` → `registration`). */
export interface StoppedRegistration {
  workflow_id: string;
  /** `MANUAL_REVIEW` (can be retried or cancelled), `WAITING` (waits for its dossier; can be retried or cancelled) or `FAILED`. */
  state: string;
  step: string | null;
  failure_code: string | null;
  failure_message: string | null;
  /** Why, in Vietnamese. */
  reason: string;
  stopped_at: ISODateTime;
  refused_command: {
    transaction_id: string;
    operation: string;
    object_key: string | null;
    result_code: number | null;
    result_message: string | null;
    not_sent: boolean;
    explanation: string | null;
    at: ISODateTime | null;
  } | null;
  /** VNNIC's own reason when it did not issue the free-program promotion code at registration. */
  vnnic_reason: string | null;
  registrant_problems: RegistrantProblem[];
  subject_id: string | null;
  subject_changed_since: boolean;
  held: { amount: string; currency: string; status: string } | null;
  work_items: { id: string; kind: string; status: string }[];
  /** `WAITING`: the dossier's status (`PENDING_IDENTITY`, …). */
  waiting_for?: string | null;
  /** A verified identity "Thử lại" would attach to the dossier. */
  reusable_verification?: { verification_id: string; subject_version_id: string; method: string; verified_at: ISODateTime } | null;
  /**
   * `FAILED`: what "Kích hoạt lại đơn" would hold again from the customer's wallet and what the
   * wallet can spend now; `refusal`: why it cannot be done (Vietnamese) other than the funds.
   */
  reactivation: { amount: string; currency: string; available: string; refusal: string | null } | null;
  can_retry: boolean;
  can_cancel: boolean;
  /** "Kích hoạt lại đơn" can be done now (nothing refuses it, the wallet holds enough). */
  can_reactivate: boolean;
}

/** `GET /domains/{key}` (key = UUID, U-label or A-label). */
export interface DomainDetail {
  domain: DomainRecord;
  registry_statuses: DomainRegistryStatus[];
  locks: DomainLock[];
  dossier: DomainDossier | null;
  /** A registration waiting for staff; null / absent otherwise. */
  registration?: StoppedRegistration | null;
  /** The extra data the TLD requires (sensitive values masked); `fields` empty for a TLD that asks nothing. */
  extra_fields?: import('./areas/core').DomainExtraFields;
}

/** `history::StoredDomainEvent`: one hash-chained domain history event. */
export interface DomainEvent {
  id: string;
  sequence: number;
  event_type: string;
  event_version: number;
  actor_type: string;
  actor_id: string;
  source: string;
  request_id: string | null;
  correlation_id: string | null;
  causation_id: string | null;
  payload: unknown;
  occurred_at: ISODateTime;
  /** Staff display name or system component; null for customer actors. */
  actor_name?: string | null;
  /** Type of the workflow named in the payload (`workflow_id`), when there is one. */
  workflow_type?: string | null;
  /** For an event the platform wrote: the staff member / customer whose request began the flow (same correlation ID in the audit trail). */
  initiated_by?: { actor_type: string; actor_id: string; actor_name: string | null } | null;
}

export interface TimelineQuery {
  /** Keyset cursor: return events with a lower sequence. */
  before?: number;
  limit?: number;
}

/** `GET /domains/{key}/timeline`, newest first, with the hash-chain verification result. */
export interface DomainTimeline {
  events: DomainEvent[];
  chain_intact: boolean;
  /** `[sequence, problem]` pairs from `history.verify_domain_chain`. */
  chain_breaks: [number, string][];
  /** The domain's scheduled actions not yet run (`workflow.scheduled_actions` PENDING / CLAIMED). */
  pending_actions?: ScheduledAction[];
}

export interface ScheduledAction {
  id: string;
  action_type: string;
  run_at: ISODateTime;
  status: 'PENDING' | 'CLAIMED';
  attempt: number;
}

/** A `registry.registry_transactions` row (payload columns excluded). */
export interface RegistryTransaction {
  id: string;
  registry_id: string;
  domain_id: string | null;
  object_type: string;
  object_key: string;
  operation: string;
  state: RegistryCommandState;
  attempt: number;
  cltrid: string;
  svtrid: string | null;
  result_code: number | null;
  result_message: string | null;
  error_class: string | null;
  request_id: string | null;
  correlation_id: string | null;
  workflow_id: string | null;
  traffic_class: string;
  /** OpenTelemetry trace recorded with the command (when tracing was active). */
  trace_id?: string | null;
  started_at: ISODateTime;
  sent_at: ISODateTime | null;
  completed_at: ISODateTime | null;
  latency_ms: number | null;
  retry_of?: string | null;
  registry_code?: string;
  registry_name?: string;
  workflow_type?: string | null;
  workflow_state?: string | null;
  /** A-label of the domain, when the command belongs to one. */
  domain_name?: string | null;
  domain_unicode_name?: string | null;
}

/**
 * `GET /registry/transactions/{id}`. The (already redacted) EPP payloads are present
 * only for callers holding `registry.payload.raw.view`; the read is then audited.
 */
export interface RegistryTransactionDetail extends RegistryTransaction {
  request_payload?: unknown;
  response_payload?: unknown;
}

/**
 * Why an exchange comes back without its text (observability-api `registry_exchange.rs`):
 * `ok` opened; `absent` none was logged; `no_field_key` no field key when written or read;
 * `too_large` over one log line; `seal_failed` / `unreadable` could not be sealed / opened.
 */
export type RegistryExchangePayloadStatus = 'ok' | 'absent' | 'no_field_key' | 'too_large' | 'seal_failed' | 'unreadable';

/**
 * One exchange with a registry or registrar, as logged on the `registry_exchange` stream.
 * Text fields the event did not carry come back as `""`, numbers as `0`.
 */
export interface RegistryExchange {
  exchange_id: string;
  registry: string;
  /** `EPP`, `REST` or `SOAP`. */
  protocol: string;
  operation: string;
  object?: string;
  cltrid: string;
  svtrid?: string;
  /** `success`, `rejected`, `unknown` (may have been applied) or `error` (not sent). */
  result: string;
  result_code?: string;
  result_message?: string;
  latency_ms?: number;
  session_id?: string;
  trace_id?: string;
  http_method?: string;
  http_path?: string;
  http_status?: number;
  service?: string;
  logged_at: ISODateTime;
  payload_status: RegistryExchangePayloadStatus;
  /** As sent / received, secrets replaced (EPP XML; REST/SOAP request line, headers, body). */
  request: string | null;
  response: string | null;
}

/**
 * `GET /ops/v1/registry-exchanges/{cltrid}` (`registry.payload.raw.view`, every view audited):
 * the exchanges of one command, read from Loki by clTRID and opened server-side.
 */
export interface RegistryExchanges {
  cltrid: string;
  from: ISODateTime;
  to: ISODateTime;
  log_query: string;
  exchanges: RegistryExchange[];
}

export type PolicyOutcome = 'ALLOW' | 'ALLOW_WITH_REQUIRED_ACTIONS' | 'BLOCK';

/** A `compliance.policy_decisions` row (minus `inputs_digest`). */
export interface PolicyDecision {
  id: string;
  operation: string;
  resource_type: string;
  resource_id: string | null;
  outcome: PolicyOutcome;
  required_actions: unknown[];
  block_reasons: unknown[];
  evaluated_policies: { policy_key: string; version: string; content_sha256?: string }[];
  request_id: string | null;
  correlation_id: string | null;
  decided_at: ISODateTime;
}

// ---------------------------------------------------------------------------
// Subjects (routes/subjects.rs)

export interface SubjectIdentifier {
  id: string;
  type: string;
  country: string | null;
  /** Masked hint, e.g. `••••1234`. The value itself needs an audited reveal. */
  hint: string | null;
}

export interface SubjectVersion {
  id: string;
  version_number: number;
  legal_name: string;
  nationality: string | null;
  country_code: string | null;
  change_reason: string | null;
  created_at: ISODateTime;
  identifiers: SubjectIdentifier[];
}

export interface SubjectVerification {
  id: string;
  subject_version_id: string;
  method: string;
  status: string;
  verified_at: ISODateTime | null;
  expires_at: ISODateTime | null;
  policy_key: string | null;
  policy_version: string | null;
  provider?: string | null;
  /** The provider's request id: VNNIC eKYC `request_id` (sent to HSTM as `ekycRequestId`). */
  provider_reference?: string | null;
}

export interface SubjectDetail {
  id: string;
  subject_type: string;
  status: string;
  current_version_id: string | null;
  managing_customer_id: string | null;
  /** The HostBill client of a subject imported from HostBill no customer manages yet. */
  hostbill_client_id?: number | null;
  /** `jsonb_agg` without COALESCE: null when the subject has no versions. */
  versions: SubjectVersion[] | null;
  verifications: SubjectVerification[];
  managing_customer_name?: string | null;
  /** Domains held by this subject (first 100 by name). */
  domains?: {
    id: string;
    ascii_name: string;
    unicode_name: string;
    lifecycle_state: LifecycleState;
    dossier_state: string;
    registry_expires_at: ISODateTime | null;
    customer_id: string;
    customer_name: string;
  }[];
  /** Dossiers naming this subject (first 100, newest first). */
  dossiers?: {
    id: string;
    domain_id: string;
    ascii_name: string;
    unicode_name: string;
    dossier_version: number;
    dossier_type: string;
    status: string;
    subject_version_id: string | null;
    subject_version_number: number | null;
    completed_at: ISODateTime | null;
    created_at: ISODateTime;
  }[];
  /** Registry contact objects created from this subject's versions. */
  contacts?: { registry_code: string; registry_name: string; handle: string; roles: string[]; domain_count: number }[];
  /** Everything stored for the current version (identity numbers masked, birth date only "on file"). */
  profile?: SubjectProfile | null;
}

/** `POST /subjects/identifiers/{id}/reveal`: the audit point for identity numbers. */
export interface RevealResponse {
  value: string;
  /** The UI re-masks the value after this many seconds. */
  expires_in_seconds: number;
}

// ---------------------------------------------------------------------------
// Global search (routes/search.rs)

export type SearchHitKind =
  | 'domain'
  | 'customer'
  | 'subject'
  | 'workflow'
  | 'registry_transaction'
  | 'domain_event'
  | 'transfer'
  | 'registrant_change'
  | 'dossier'
  | 'contact';

export interface SearchHit {
  kind: SearchHitKind;
  /** For `domain_event` hits this is the DOMAIN id (the event matched by request/correlation id). */
  id: string;
  /** Readable name (domain, customer, subject…); null for a subject the caller may not name. */
  label: string | null;
  /** Lifecycle / status of the hit, when it has one. */
  state?: string;
  /** The domain the hit belongs to (transactions, workflows, events, transfers, dossiers). */
  domain_id?: string | null;
  /** Secondary line: domain name, code, type or direction. */
  detail?: string | null;
  /** On contact hits: the registry code (contacts are identified by registry + handle). */
  registry_code?: string;
}

export interface SearchResponse {
  query: string;
  results: SearchHit[];
}

// ---------------------------------------------------------------------------
// Four-eyes approvals and audit (routes/approvals.rs)

export type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED' | 'EXECUTED' | 'CANCELLED';

export interface ApprovalRequest {
  id: string;
  action: string;
  /** What the guarded action is, in words. */
  action_description: string | null;
  /** The action also requires an MFA-verified session. */
  action_sensitive: boolean;
  resource_type: string;
  resource_id: string;
  /** Name of the record the request would change, when it has one. */
  resource_name: string | null;
  payload: unknown;
  reason: string;
  status: ApprovalStatus;
  requested_by: string;
  requested_by_name: string | null;
  /** Staff address in full; a customer account only as a hint (`j***@example.vn`). */
  requested_by_email: string | null;
  /** The signed-in operator asked for this one, so they may never decide it. */
  requested_by_self: boolean;
  requested_at: ISODateTime;
  decided_by: string | null;
  decided_by_name: string | null;
  decided_by_email: string | null;
  decided_at: ISODateTime | null;
  decision_reason: string | null;
  expires_at: ISODateTime;
  executed_at: ISODateTime | null;
}

/** Keyset page: `next_before` is the last item's id when the page was full. */
export interface ApprovalPage {
  items: ApprovalRequest[];
  next_before: string | null;
}

export interface ApprovalQueueQuery {
  /** One status, or `ALL`. Omitted means the live queue: pending and not yet expired. */
  status?: ApprovalStatus | 'ALL';
  action?: string;
  before?: string;
  limit?: number;
}

export interface NewApproval {
  /** Permission code of the guarded action, e.g. `compliance.legal_unlock`. */
  action: string;
  resource_type: string;
  resource_id: string;
  payload: unknown;
  reason: string;
}

export interface ApprovalDecisionRequest {
  approve: boolean;
  reason: string;
}

export interface ApprovalStatusChange {
  id: string;
  status: ApprovalStatus;
}

export type AuditOutcome = 'SUCCESS' | 'DENIED' | 'FAILED';

export interface AuditLogEntry {
  id: string;
  actor_type: string;
  actor_id: string;
  /** Name of the person who acted; null for an automated actor, whose `actor_id` is its name. */
  actor_display_name: string | null;
  /** Staff address in full; a customer account only as a hint (`j***@example.vn`). */
  actor_email: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  /** Name of the record acted on, when it has one. */
  resource_name: string | null;
  outcome: AuditOutcome;
  before: unknown;
  after: unknown;
  reason: RecordedReason | null;
  request_id: string | null;
  correlation_id: string | null;
  created_at: ISODateTime;
}

/** Keyset page: `next_before` is the last item's id when the page was full. */
export interface AuditPage {
  items: AuditLogEntry[];
  next_before: string | null;
}

export interface AuditQuery {
  resource_type?: string;
  resource_id?: string;
  actor_id?: string;
  actor_type?: string;
  /** Prefix of the action code, e.g. `domain.` or `approval.decide`. */
  action?: string;
  outcome?: AuditOutcome;
  correlation_id?: string;
  before?: string;
  limit?: number;
}

// ---------------------------------------------------------------------------
// Abuse cases (routes/cases.rs, crates/cases/src/abuse.rs; spec §44)

export type AbuseCaseStatus = 'NEW' | 'TRIAGED' | 'INVESTIGATING' | 'ACTION_REQUIRED' | 'WAITING' | 'RESOLVED' | 'CLOSED';
export type AbuseCategory = 'SPAM' | 'PHISHING' | 'MALWARE' | 'BOTNET' | 'PHARMING' | 'FRAUD' | 'CSAM' | 'TRADEMARK' | 'OTHER';
export type AbusePriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type AbuseSource = 'REPORT' | 'LAW_ENFORCEMENT' | 'REGISTRY' | 'ICANN' | 'INTERNAL';
export type AbuseChannel = 'WEB_FORM' | 'EMAIL' | 'API' | 'PHONE' | 'POSTAL' | 'INTERNAL';
export type ReporterType = 'ANONYMOUS' | 'INDIVIDUAL' | 'ORGANIZATION' | 'LAW_ENFORCEMENT' | 'REGISTRY' | 'ICANN' | 'INTERNAL';
export type LawEnforcementStatus = 'RECEIVED' | 'ACKNOWLEDGED' | 'RESPONDED' | 'REJECTED' | 'CLOSED';
export type DomainActionKind = 'SUSPEND' | 'UNSUSPEND' | 'LOCK' | 'UNLOCK' | 'NOTE_ONLY';
export type DomainActionStatus = 'PENDING' | 'APPLIED' | 'FAILED' | 'UNKNOWN';

/** `to_jsonb(compliance.abuse_cases)`; list rows add `overdue` and `report_count`. */
export interface AbuseCase {
  id: string;
  case_number: string;
  domain_id: string | null;
  reported_name: string;
  category: AbuseCategory;
  priority: AbusePriority;
  source: AbuseSource;
  status: AbuseCaseStatus;
  sla_status: 'SET' | 'POLICY_MISSING';
  sla_policy_key: string | null;
  sla_policy_version: string | null;
  first_response_due_at: ISODateTime | null;
  first_responded_at: ISODateTime | null;
  assigned_to: string | null;
  resolution: string | null;
  opened_at: ISODateTime;
  status_changed_at: ISODateTime;
  resolved_at: ISODateTime | null;
  closed_at: ISODateTime | null;
  version: number;
  overdue?: boolean;
  report_count?: number;
}

export interface AbuseCaseFilter {
  status?: AbuseCaseStatus;
  category?: AbuseCategory;
  domain?: string;
  overdue?: boolean;
  limit?: number;
}

export interface AbuseReport {
  id: string;
  channel: AbuseChannel;
  reporter_type: ReporterType;
  /** The contact itself is encrypted at rest and never returned. */
  has_reporter_contact: boolean;
  category: string;
  summary: string;
  external_reference: string | null;
  received_at: ISODateTime;
  recorded_by_type: string;
  recorded_by_id: string;
}

export interface AbuseEvidence {
  id: string;
  document_id: string | null;
  url: string | null;
  sha256: string;
  description: string;
  captured_at: ISODateTime;
  added_by_type: string;
  added_by_id: string;
  created_at: ISODateTime;
}

export interface CaseAction {
  id: string;
  action: string;
  from_status: string | null;
  to_status: string | null;
  details: unknown;
  actor_type: string;
  actor_id: string;
  request_id: string | null;
  correlation_id: string | null;
  created_at: ISODateTime;
}

export interface DomainAction {
  id: string;
  case_id: string;
  domain_id: string;
  action: DomainActionKind;
  status: DomainActionStatus;
  reason: string;
  reverses_action_id: string | null;
  registry_transaction_id: string | null;
  lock_id: string | null;
  result_code: number | null;
  result_message: string | null;
  requested_by_type: string;
  requested_by_id: string;
  requested_at: ISODateTime;
  completed_at: ISODateTime | null;
}

export interface LawEnforcementRequest {
  id: string;
  case_id: string;
  agency: string;
  jurisdiction: string;
  reference: string;
  legal_basis: string;
  received_at: ISODateTime;
  deadline_at: ISODateTime | null;
  status: LawEnforcementStatus;
  response_summary: string | null;
  responded_at: ISODateTime | null;
}

export interface AbuseCaseDetail extends AbuseCase {
  reports: AbuseReport[];
  evidence: AbuseEvidence[];
  actions: CaseAction[];
  domain_actions: DomainAction[];
  law_enforcement_requests: LawEnforcementRequest[];
  /** Staff name of the assignee, never an e-mail. */
  assigned_to_display_name: string | null;
  /** `actor_id` -> staff name, for the history entries. Unknown actors are absent. */
  actor_display_names: Record<string, string>;
}

export interface NewAbuseReport {
  domain: string;
  category: AbuseCategory;
  priority: AbusePriority;
  source: AbuseSource;
  channel: AbuseChannel;
  reporter_type: ReporterType;
  /** Personal data: encrypted server-side, never returned. */
  reporter_contact?: string;
  summary: string;
  external_reference?: string | null;
  received_at?: ISODateTime | null;
}

export interface OpenedAbuseCase {
  case_id: string;
  case_number: string;
  report_id: string;
  /** False when the report was attached to an existing open case (dedupe). */
  created: boolean;
  status: string;
  sla_status: string;
  first_response_due_at: ISODateTime | null;
}

export interface AbuseTransitionRequest {
  to: AbuseCaseStatus;
  /** Optimistic concurrency: the case version the operator was looking at. */
  version?: number;
  note?: string;
  /** Required for RESOLVED / CLOSED unless the case already has one. */
  resolution?: string;
}

export interface NewEvidence {
  document_id?: string | null;
  url?: string | null;
  /** SHA-256 (hex) of the captured content. */
  sha256: string;
  description: string;
  captured_at?: ISODateTime | null;
}

export interface NewLawEnforcementRequest {
  agency: string;
  /** ISO 3166-1 alpha-2, optionally with a subdivision (`US-CA`). */
  jurisdiction: string;
  reference: string;
  legal_basis: string;
  received_at?: ISODateTime | null;
  deadline_at?: ISODateTime | null;
}

export interface LawEnforcementUpdate {
  status: LawEnforcementStatus;
  response_summary?: string | null;
}

export interface DomainActionRequest {
  action: DomainActionKind;
  reason: string;
}

// ---------------------------------------------------------------------------
// Disputes and legal locks (routes/cases.rs, crates/cases/src/disputes.rs; spec §45)

export type DisputeStatus = 'OPENED' | 'LOCKED' | 'DECIDED' | 'IMPLEMENTED' | 'WITHDRAWN' | 'CLOSED';
export type DisputeType = 'UDRP' | 'URS' | 'COURT_ORDER' | 'REGISTRY_ACTION' | 'GOVERNMENT_REQUEST' | 'OTHER';
export type DisputeDecision = 'TRANSFER' | 'CANCELLATION' | 'SUSPENSION' | 'DENIED' | 'SETTLED' | 'OTHER';

/** Four-eyes action that authorises releasing a dispute's legal lock. */
export const LEGAL_UNLOCK_ACTION = 'compliance.legal_unlock';
export const DISPUTE_APPROVAL_RESOURCE = 'domain_dispute';

export interface Dispute {
  id: string;
  domain_id: string;
  /** A-label, joined in by the list and detail reads. */
  domain?: string;
  dispute_type: DisputeType;
  provider: string;
  case_reference: string;
  status: DisputeStatus;
  lock_type: string;
  lock_id: string | null;
  abuse_case_id: string | null;
  summary?: string | null;
  decision: DisputeDecision | null;
  decision_summary: string | null;
  decided_at: ISODateTime | null;
  implemented_at: ISODateTime | null;
  release_approval_request_id: string | null;
  close_reason?: string | null;
  opened_at: ISODateTime;
  closed_at: ISODateTime | null;
  /** List rows only. */
  lock_active?: boolean;
  /** Detail only: the `domain.domain_locks` row. */
  lock?: (DomainLock & { released_at: ISODateTime | null; release_reason: RecordedReason | null }) | null;
  /** Detail only: the abuse case this dispute came from, by case number. */
  abuse_case?: { id: string; case_number: string; status: AbuseCaseStatus; category: AbuseCategory } | null;
}

export interface DisputeFilter {
  status?: DisputeStatus;
  domain_id?: string;
  limit?: number;
}

export interface NewDispute {
  domain_id: string;
  dispute_type: DisputeType;
  provider: string;
  case_reference: string;
  summary?: string | null;
  abuse_case_id?: string | null;
}

export interface DisputeDecisionRequest {
  decision: DisputeDecision;
  decision_summary?: string | null;
}

export interface DisputeCloseRequest {
  outcome: 'CLOSED' | 'WITHDRAWN';
  /** APPROVED four-eyes `compliance.legal_unlock` request for this dispute. Always required. */
  approval_request_id: string;
  reason: string;
}

// ---------------------------------------------------------------------------
// Registration-data disclosure (routes/disclosure.rs; spec §41)

export type DisclosureStatus =
  | 'RECEIVED'
  | 'UNDER_REVIEW'
  | 'INFO_REQUESTED'
  | 'APPROVED'
  | 'PARTIALLY_APPROVED'
  | 'DENIED'
  | 'RELEASED'
  | 'CLOSED';

export type DisclosureRequesterType =
  | 'LAW_ENFORCEMENT'
  | 'IP_RIGHTS_HOLDER'
  | 'CYBERSECURITY'
  | 'CONSUMER_PROTECTION'
  | 'LEGAL_PROCEEDING'
  | 'OTHER';

/** Field keys accepted by `registration_data::publication::Field`. */
export const DISCLOSURE_FIELDS = [
  'domain.name',
  'domain.handle',
  'domain.status',
  'domain.registered_at',
  'domain.expires_at',
  'domain.last_changed_at',
  'domain.nameservers',
  'domain.dnssec',
  'registrar.identity',
  'registrar.abuse_contact',
  'registrant.name',
  'registrant.organization',
  'registrant.street',
  'registrant.city',
  'registrant.province',
  'registrant.postal_code',
  'registrant.country',
  'registrant.phone',
  'registrant.email',
] as const;

export type DisclosureField = (typeof DISCLOSURE_FIELDS)[number];

export interface DisclosureCaseSummary {
  id: string;
  case_number: string;
  /** Null when the name is not one we manage. */
  domain_id: string | null;
  domain_name: string;
  requester_type: DisclosureRequesterType;
  requester_organization: string | null;
  status: DisclosureStatus;
  requested_fields: string[];
  received_at: ISODateTime;
  deadline_at: ISODateTime | null;
  overdue: boolean;
}

export interface DisclosureRelease {
  id: string;
  fields: string[];
  payload_sha256: string;
  delivery_channel: string;
  released_by_type: string;
  released_by_id: string;
  /** Staff name of whoever released the data, never an e-mail. */
  released_by_display_name: string | null;
  released_at: ISODateTime;
}

/** One disclosure case with its release log (digests only). */
export interface DisclosureCase {
  id: string;
  case_number: string;
  requester_name: string;
  /** The requester is a natural person: only a hint of their e-mail is ever returned. */
  requester_email_hint: string;
  requester_organization: string | null;
  requester_type: DisclosureRequesterType;
  legal_basis: string;
  purpose: string;
  requested_fields: string[];
  domain_id: string | null;
  domain_name: string;
  supporting_document_ids: string[];
  status: DisclosureStatus;
  received_at: ISODateTime;
  deadline_at: ISODateTime | null;
  policy_key: string | null;
  policy_version: string | null;
  created_by: string;
  /** Staff name of whoever registered the request; null for a system or unknown actor. */
  created_by_display_name: string | null;
  reviewer_id: string | null;
  /** Staff name of the decision maker, never an e-mail. */
  reviewer_display_name: string | null;
  decided_at: ISODateTime | null;
  decision_reason: string | null;
  approved_fields: string[];
  released_at: ISODateTime | null;
  closed_at: ISODateTime | null;
  releases: DisclosureRelease[];
}

export interface DisclosureListQuery {
  status?: DisclosureStatus;
  limit?: number;
}

export interface NewDisclosureRequest {
  requester_name: string;
  requester_email: string;
  requester_organization?: string | null;
  requester_type: DisclosureRequesterType;
  legal_basis: string;
  purpose: string;
  requested_fields: string[];
  domain_name: string;
  supporting_document_ids?: string[];
  /** Imminent threat to life, serious injury, critical infrastructure or child exploitation. */
  urgent?: boolean;
}

export interface OpenedDisclosureCase {
  id: string;
  case_number: string;
  /** Null when no disclosure policy is in force. */
  deadline_at: ISODateTime | null;
  domain_id: string | null;
}

export interface DisclosureDecisionRequest {
  /** Subset of the requested fields; empty means deny. */
  approved_fields: string[];
  reason: string;
}

export interface DisclosureStatusChange {
  id: string;
  status: DisclosureStatus;
}

/** Returned ONCE by the release call. Only the digest is stored server-side. */
export interface DisclosureReleaseResult {
  case_id: string;
  fields: string[];
  data: Record<string, unknown>;
  payload_sha256: string;
}

// ---------------------------------------------------------------------------
// Observability API (/ops/v1, subsystems/platform/facade/adapters/observability-api/src/routes.rs)

export interface JobQueueSummary {
  queue: string;
  ready: number;
  running: number;
  scheduled: number;
  retrying: number;
  dead: number;
  oldest_ready_at: ISODateTime | null;
  max_attempts_seen: number | null;
}

/** `GET /ops/v1/overview` (cached server-side for 15 s). */
export interface OpsOverview {
  /** Open registry transactions by state (PENDING, SENT, UNKNOWN, ...). */
  registry_transactions_open: Record<string, number>;
  reconciliation_issues_open: number;
  /** In-flight workflows by lower-case state. */
  workflows_in_flight: Record<string, number>;
  queues: JobQueueSummary[];
  outbox_pending: number;
  /** Journal entries whose postings do not balance. The ledger invariant requires zero. */
  ledger_unbalanced_entries: number;
  /** VNNIC coverage exceptions by gap. */
  compliance_gaps: Record<string, number>;
  incidents_open: number;
  generated_at: ISODateTime;
}

export interface OpsService {
  service: string | null;
  instance: string | null;
  up: boolean;
}

export interface OpsRegistry {
  code: string;
  name: string;
  open_transactions: number;
  unknown_transactions: number;
  open_issues: number;
  last_success_at: ISODateTime | null;
  last_failure_at: ISODateTime | null;
}

export interface OpsQueues {
  scheduled_actions: JobQueueSummary[];
  outbox: { event_type: string; pending: number; oldest_created_at: ISODateTime | null }[];
  workflows: { workflow_type: string; state: string; count: number; oldest: ISODateTime | null }[];
}

/** Alertmanager API v2 alert, passed through unchanged. */
export interface AlertmanagerAlert {
  labels: Record<string, string>;
  annotations: Record<string, string>;
  startsAt: ISODateTime;
  endsAt?: ISODateTime;
  updatedAt?: ISODateTime;
  fingerprint: string;
  generatorURL?: string;
  status: { state: 'active' | 'suppressed' | 'unprocessed'; silencedBy: string[]; inhibitedBy: string[] };
  receivers?: { name: string }[];
}

/** Prometheus `data` object, passed through unchanged. */
export type PromSampleValue = [number, string];
export interface PromVectorSample {
  metric: Record<string, string>;
  value: PromSampleValue;
}
export interface PromMatrixSeries {
  metric: Record<string, string>;
  values: PromSampleValue[];
}
export type PromQueryData =
  | { resultType: 'vector'; result: PromVectorSample[] }
  | { resultType: 'matrix'; result: PromMatrixSeries[] }
  | { resultType: 'scalar' | 'string'; result: PromSampleValue };

export interface MetricsInstantRequest {
  query: string;
  time?: ISODateTime;
}

export interface MetricsRangeRequest {
  query: string;
  start: ISODateTime;
  end: ISODateTime;
  /** Seconds. The server derives one (>= 15 s, <= 11,000 points) when omitted. */
  step?: number;
}

export interface LogsQueryRequest {
  /** LogQL. */
  query: string;
  start: ISODateTime;
  end: ISODateTime;
  limit?: number;
}

export interface LogLine {
  timestamp: ISODateTime;
  labels: Record<string, string>;
  /** Scrubbed server-side (key-based redaction, then emails and long digit runs). */
  line: string;
  links: { trace_id: string | null; correlation_id: string | null };
}

export interface LogsResponse {
  items: LogLine[];
  scrubbed: boolean;
}

export interface TraceSearchRequest {
  /** Approved attributes only: service.name, registry, operation, workflow.type, queue, http.route, error.type, correlation_id, request_id. */
  tags: Record<string, string>;
  start: ISODateTime;
  end: ISODateTime;
  limit?: number;
}

/** Tempo `/api/search` response, passed through unchanged. */
export interface TempoSearchResponse {
  traces?: {
    traceID: string;
    rootServiceName?: string;
    rootTraceName?: string;
    startTimeUnixNano?: string;
    durationMs?: number;
  }[];
}

/** Tempo `/api/traces/{id}` OTLP-JSON (`batches` or `resourceSpans`), passed through unchanged. */
export type TempoTrace = unknown;

/** `GET /ops/v1/correlate/{correlation_id}` (spec §116). */
export interface Correlation {
  correlation_id: string;
  domain_events: { domain_id: string; domain: string; sequence: number; event_type: string; occurred_at: ISODateTime; request_id: string | null }[];
  workflows: {
    id: string;
    workflow_type: string;
    state: string;
    current_step: string | null;
    resource_type: string | null;
    resource_id: string | null;
    /** Readable name of the resource (a domain name), when it has one. */
    resource_name: string | null;
    failure_code: string | null;
  }[];
  registry_transactions: {
    id: string;
    operation: string;
    state: RegistryCommandState;
    cltrid: string;
    svtrid: string | null;
    result_code: number | null;
    started_at: ISODateTime;
    completed_at: ISODateTime | null;
    workflow_id: string | null;
  }[];
  audit: { action: string; resource_type: string; resource_id: string | null; actor_type: string; outcome: string; created_at: ISODateTime }[];
  events_published: { event_type: string; status: string; created_at: ISODateTime }[];
  suggested_queries: { logs: string; traces: { tags: Record<string, string> } };
}

/** `GET /ops/v1/domains/{name}/links`: operational context for one domain. */
export interface DomainLinks {
  domain_id: string;
  domain: string;
  lifecycle_state: string;
  workflows: { id: string; workflow_type: string; state: string; current_step: string | null; correlation_id: string | null; updated_at: ISODateTime }[];
  registry_transactions: { id: string; operation: string; state: RegistryCommandState; correlation_id: string | null; started_at: ISODateTime }[];
  open_issues: { id: string; issue_type: string; severity: string; detected_at: ISODateTime }[];
  recent_events: { sequence: number; event_type: string; occurred_at: ISODateTime; correlation_id: string | null }[];
}

export type IncidentSeverity = 'P0' | 'P1' | 'P2' | 'P3';
export type IncidentStatus = 'OPEN' | 'ACKNOWLEDGED' | 'MITIGATING' | 'MONITORING' | 'RESOLVED';

/** `ops.ops_incidents` row (spec §118). */
export interface Incident {
  id: string;
  severity: IncidentSeverity;
  title: string;
  status: IncidentStatus;
  started_at: ISODateTime;
  acknowledged_at: ISODateTime | null;
  resolved_at: ISODateTime | null;
  owner: string | null;
  /** Owner shown to people; `owner` stays for the copyable ID field. */
  owner_name: string | null;
  owner_email: string | null;
  root_cause: string | null;
  resolution: string | null;
  related_alerts: string[];
  related_services: string[];
  version: number;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

/** One audited move in an incident's history. */
export interface IncidentTimelineEntry {
  at: ISODateTime;
  action: string;
  outcome: string;
  actor_name: string | null;
  actor_email: string | null;
  reason: string | null;
}

/** `GET /ops/v1/incidents/{id}`: the incident plus who moved it, when and why. */
export interface IncidentDetail {
  incident: Incident;
  timeline: IncidentTimelineEntry[];
}

export interface NewIncident {
  severity: IncidentSeverity;
  title: string;
  related_alerts?: string[];
  related_services?: string[];
}

export interface IncidentTransitionRequest {
  to: IncidentStatus;
  root_cause?: string | null;
  /** Required to move to RESOLVED. */
  resolution?: string | null;
}

/** `GET /ops/v1/slo`: definitions from observability/slo/slos.yaml plus the current SLI. */
export interface Slo {
  id: string | null;
  name: string | null;
  service: string | null;
  /** As written in the YAML: a ratio (0.999) or a percentage (99.9). */
  objective: number | string | null;
  window: string | null;
  version: number | string | null;
  /** Prometheus sample value (a decimal string), when a metrics backend is configured. */
  current?: string | null;
  current_error?: string;
}

/**
 * `GET|PUT /ops/v1/settings/log-retention` (Cài đặt › Bảo mật › Lưu trữ log): how many days each
 * log class stays in Loki. Loki's global retention (`max_days`) is the upper bound.
 */
export interface LogRetention {
  registry_exchange_days: number;
  other_days: number;
  min_days: number;
  max_days: number;
  updated_at: ISODateTime;
  /** Staff user id of the last change; null: the migration's defaults. */
  updated_by: string | null;
}

export interface LogRetentionUpdate {
  registry_exchange_days: number;
  other_days: number;
  reason: string;
}

// VNNIC compliance (/admin/v1/vnnic, master spec §52 and §106)

/** Explicit exception queues: every managed domain not fully covered sits in at least one. */
export type VnnicGap =
  | 'MISSING_DOSSIER'
  | 'INCOMPLETE_DOSSIER'
  | 'VERIFICATION_NOT_VALID'
  | 'SUBJECT_MISMATCH'
  | 'VNNIC_SYNC_MISSING'
  | 'VNNIC_SYNC_PENDING'
  | 'VNNIC_SYNC_REJECTED'
  | 'VNNIC_SYNC_ERROR'
  | 'REPORT_PROJECTION_MISSING'
  /** Managed .vn name absent from VNNIC's `GET /domains` list (latest completed comparison). */
  | 'VNNIC_REGISTRY_MISSING';

/** State of a dossier's synchronisation with VNNIC (.vn names). */
export type VnnicSyncStatus =
  | 'NOT_SYNCED'
  | 'SYNC_PENDING'
  | 'QUEUED'
  | 'SUBMITTING'
  | 'SUBMITTED'
  | 'ACKNOWLEDGED'
  | 'REJECTED'
  | 'CORRECTION_REQUIRED'
  | 'RESUBMIT_PENDING'
  | 'SYNC_ERROR'
  | 'SUPERSEDED';

/**
 * The error code recorded on a dossier sync or one of its transactions (the platform's own, or
 * VNNIC's as received): a string on the wire. Opaque on purpose: the type checker refuses to render
 * it or add it to text, so a page shows it only through its meaning (SyncErrorCode).
 */
export type VnnicSyncErrorCode = unknown;

/**
 * A reason recorded on a record the platform itself may write — an audit entry, a domain lock, a
 * work item or add-on it closes, a dossier status change: either its technical English
 * ("post-registration lock (transfer policy)", lib/systemReason.ts) or a person's text. Opaque on
 * purpose: the type checker refuses to render it raw, so a page shows it only through
 * <SystemReason> (or `useSystemReason()` where it needs the text).
 */
export type RecordedReason = unknown;

/**
 * What a dossier still misses, as a status change records it (`identity_verification`,
 * `document:<type>`, `hstm:<types>:<signed>`, …; lib/missingItem.ts). Opaque on purpose: the type
 * checker refuses to render it raw, so a page shows it only through <MissingItems>.
 */
export type MissingItem = unknown;

/**
 * An add-on's catalogue code (config/addons.yaml). A page names the add-on (`useAddonName`), the
 * code at most beside it: tests/addon-names.test.mjs reads the fields declared with this type.
 */
export type AddonCode = string;

/** State of a single sync transaction. UNKNOWN is resolved by a recorded outcome, never by a blind retry. */
export type VnnicTransactionStatus =
  | 'SUBMITTING'
  | 'SUBMITTED'
  | 'ACKNOWLEDGED'
  | 'REJECTED'
  | 'CORRECTION_REQUIRED'
  | 'FAILED'
  | 'UNKNOWN';

export type VnnicReportType = '0805A' | '0805B';

/** No official VNNIC template is on file yet, so every schema is currently `UNOFFICIAL_DRAFT`. */
export type VnnicSchemaStatus = 'UNOFFICIAL_DRAFT' | 'OFFICIAL';

export type VnnicReportStatus =
  | 'GENERATED'
  | 'VALIDATION_FAILED'
  | 'VALIDATED'
  | 'EXPORTED'
  | 'SUBMITTED'
  | 'ACKNOWLEDGED'
  | 'REJECTED'
  | 'SUPERSEDED';

/** Latest state of each report period, as embedded in the coverage summary. */
export interface VnnicReportState {
  report_type: VnnicReportType;
  period_start: ISODateTime;
  period_end: ISODateTime;
  revision: number;
  status: VnnicReportStatus;
  record_count: number;
  invalid_count: number;
  schema_version: string;
  generated_at: ISODateTime;
}

/** A province of VNNIC's place list (`GET /reference/vn-places/provinces`). */
export interface VnProvince {
  id: number;
  alpha3_code: string | null;
  full_name: string;
  place_type: string | null;
}

/** A ward of VNNIC's place list (`GET /reference/vn-places/provinces/{id}/wards`). */
export interface VnWard {
  id: number;
  province_id: number;
  name: string;
  place_type: string | null;
}

/** `GET /reference/vn-places/status`. */
export interface VnPlacesStatus {
  provinces: number;
  wards: number;
  retired: number;
  wards_from_vnnic: number;
  syncs: {
    id: string;
    started_at: ISODateTime;
    finished_at: ISODateTime | null;
    outcome: 'RUNNING' | 'SUCCEEDED' | 'FAILED';
    provinces: number | null;
    wards: number | null;
    retired: number | null;
    error_code: string | null;
    requested_by: string | null;
  }[];
}

/**
 * `GET /vnnic/enterprises/{gdt_code}` and `GET /vnnic/business-households/{tax_code}` (needs
 * `business_registry.lookup`; audited): VNNIC's answer, identity numbers masked by the server.
 */
export interface VnnicBusinessLookup {
  found: boolean;
  http_status: number;
  /** VNNIC's EnterpriseInfoDTO / BusinessInfoDTO (docs/registries/vnnic-registrar-api-2.2.0.json). */
  result: Record<string, unknown> | null;
}

/** `POST /vnnic/verifications/{id}/registry-lookup`: what was looked up and found (the answer is kept as evidence). */
export interface VerificationRegistryLookup {
  verification_id: string;
  endpoint: string;
  identifier_type: string;
  /** An enterprise's tax code as it is; a household's (its owner's CCCD) masked. */
  identifier_hint: string;
  found: boolean;
  /** Whether the registered name is the subject's legal name; null when the answer has no name. */
  name_matches: boolean | null;
}

export interface VnnicCoverage {
  managed_domains: number;
  fully_covered: number;
  gaps: Record<VnnicGap, number>;
  /** Counts by dossier status; statuses with no dossiers are omitted. */
  dossiers: Partial<Record<DossierStatus, number>>;
  /** Counts by sync status; statuses with no rows are omitted. */
  sync: Partial<Record<VnnicSyncStatus, number>>;
  oldest_unacknowledged_sync_at: ISODateTime | null;
  /** International names projected into the reporting set. */
  projected_international: number;
  vn_managed: number;
  vn_dossier_complete: number;
  vn_acknowledged: number;
  reports: VnnicReportState[];
}

export interface VnnicException {
  domain_id: string;
  ascii_name: string;
  tld_class: string;
  gap: VnnicGap;
}

export interface VnnicExceptionQuery {
  gap?: VnnicGap;
  limit?: number;
}

/** One comparison of VNNIC's domain list (Registrar API `GET /domains`) with the local .vn domains. */
export interface VnnicRegistryListCheck {
  id: string;
  environment: 'OTE' | 'PRODUCTION';
  status: 'COMPLETED' | 'FAILED';
  fetched_at: ISODateTime;
  /** Names in VNNIC's answer, and VNNIC's own `numberOfDomain`. */
  vnnic_count: number | null;
  vnnic_reported_count: number | null;
  /** Local .vn names expected at VNNIC. */
  local_count: number | null;
  missing_at_vnnic: number | null;
  unknown_locally: number | null;
  grace_seconds: number;
  response_sha256: string | null;
  http_status: number | null;
  error_code: string | null;
  error_message: string | null;
}

export type VnnicRegistryMismatchKind = 'MISSING_AT_VNNIC' | 'UNKNOWN_LOCALLY';

export interface VnnicRegistryMismatch {
  ascii_name: string;
  kind: VnnicRegistryMismatchKind;
  domain_id: string | null;
  local_state: string | null;
  vnnic_created_date: string | null;
  vnnic_expired_date: string | null;
}

/** `GET /vnnic/registry-list`: the latest attempt, the latest completed comparison and its mismatches. */
export interface VnnicRegistryList {
  latest: VnnicRegistryListCheck | null;
  latest_completed: VnnicRegistryListCheck | null;
  mismatches: VnnicRegistryMismatch[];
}

export interface VnnicRegistryListQuery {
  kind?: VnnicRegistryMismatchKind;
  limit?: number;
}

export type VnnicSyncOperation = 'SUBMIT' | 'UPDATE';

export interface VnnicSyncRow {
  dossier_id: string;
  domain_id: string;
  domain: string;
  /** Dossier version and status, so the row names the dossier instead of showing its id. */
  dossier_version: number;
  dossier_status: DossierStatus;
  operation: VnnicSyncOperation;
  status: VnnicSyncStatus;
  attempts: number;
  external_reference: string | null;
  last_transaction_id: string | null;
  last_error_code: VnnicSyncErrorCode | null;
  next_attempt_at: ISODateTime | null;
  status_changed_at: ISODateTime;
}

export interface VnnicSyncQuery {
  status?: VnnicSyncStatus;
  limit?: number;
}

/** Evidence of a recorded VNNIC answer (sync transaction or report batch). */
export interface VnnicReceipt {
  outcome: string;
  external_reference: string | null;
  evidence_object_key: string | null;
  note: string | null;
  recorded_by_id: string | null;
  /** Staff name of whoever recorded the answer, never an e-mail. */
  recorded_by_display_name?: string | null;
  recorded_at: ISODateTime;
}

export interface VnnicSyncTransaction {
  id: string;
  domain_id: string;
  /** A-label of the name this attempt is for; the detail read joins it in. */
  domain_name: string;
  unicode_name: string;
  dossier_id: string;
  dossier_version: number;
  dossier_status: DossierStatus;
  /** State of the dossier's synchronisation as a whole, when a sync row exists. */
  sync_status: VnnicSyncStatus | null;
  operation: VnnicSyncOperation;
  adapter: string;
  schema_version: string;
  /** Present only for callers holding `regulatory_report.manage`. */
  request_payload?: unknown;
  response_payload: unknown;
  status: VnnicTransactionStatus;
  attempt: number;
  external_reference: string | null;
  submitted_at: ISODateTime | null;
  completed_at: ISODateTime | null;
  error_code: VnnicSyncErrorCode | null;
  error_message: string | null;
  receipts: VnnicReceipt[];
}

/** Every recorded outcome must reference stored evidence (e.g. the VNNIC reply or a portal screenshot). */
interface VnnicEvidence {
  evidence_object_key: string;
  note?: string;
}

export type VnnicSyncOutcomeRequest = VnnicEvidence &
  (
    | { outcome: 'ACKNOWLEDGED'; reference: string }
    | { outcome: 'REJECTED' | 'CORRECTION_REQUIRED'; code: string; message: string }
  );

export interface VnnicStatusChange<S> {
  id: string;
  status: S;
}

export interface VnnicResubmitResponse {
  dossier_id: string;
  status: 'RESUBMIT_PENDING';
}

export interface VnnicReportBatch {
  id: string;
  report_type: VnnicReportType;
  period_start: ISODateTime;
  period_end: ISODateTime;
  schema_id: string;
  schema_version: string;
  schema_status: VnnicSchemaStatus;
  revision: number;
  supersedes_batch_id: string | null;
  status: VnnicReportStatus;
  record_count: number;
  invalid_count: number;
  checksum: string | null;
  export_object_key: string | null;
  external_reference: string | null;
  generated_by: string | null;
  generated_at: ISODateTime;
  exported_at: ISODateTime | null;
  submitted_at: ISODateTime | null;
  acknowledged_at: ISODateTime | null;
}

export interface VnnicReportQuery {
  report_type?: VnnicReportType;
  limit?: number;
}

export interface VnnicReportItem {
  line_number: number;
  domain_id: string;
  /** A-label of the reported name; the batch detail joins it in. */
  domain_name: string;
  unicode_name: string;
  change_type: string;
  /** Present only for callers holding `regulatory_report.manage`. */
  payload?: unknown;
  validation_status: 'VALID' | 'INVALID';
  validation_errors: string[];
  submission_status: string | null;
  error_code: string | null;
  error_message: string | null;
}

export interface VnnicReportBatchDetail extends VnnicReportBatch {
  /** Whether the stored checksum still matches the batch items. */
  checksum_verified: boolean;
  /** Staff name of whoever generated the batch, never an e-mail. */
  generated_by_display_name: string | null;
  items: VnnicReportItem[];
  receipts: VnnicReceipt[];
}

export type VnnicGenerateReportRequest = { report_code: '0805A'; year: number } | { report_code: '0805B'; year: number; quarter: 1 | 2 | 3 | 4 };

export interface VnnicGenerateReportResponse {
  id: string;
  report_type: VnnicReportType;
  revision: number;
  status: VnnicReportStatus;
  record_count: number;
  invalid_count: number;
  checksum: string | null;
  schema_version: string;
  schema_status: VnnicSchemaStatus;
}

export interface VnnicExportResponse {
  id: string;
  status: VnnicReportStatus;
  adapter: string;
}

export interface VnnicItemError {
  line_number: number;
  code: string;
  message: string;
}

export type VnnicReportOutcomeRequest = VnnicEvidence &
  (
    | { outcome: 'SUBMITTED'; reference: string | null }
    | { outcome: 'ACKNOWLEDGED'; reference: string }
    | { outcome: 'REJECTED'; code: string; message: string; item_errors: VnnicItemError[] }
  );

// ---------------------------------------------------------------------------
// The surface the UI consumes; implemented by the HTTP client and by the mocks.

export interface CallOptions {
  signal?: AbortSignal;
}

export interface WriteOptions extends CallOptions {
  /** Reuse the same key when retrying the same logical write. */
  idempotencyKey?: string;
  /**
   * "Gửi hook cho khách": `false` keeps this write's webhooks from the owning customer's
   * endpoints (sent as `notify_customer`); undefined = the server default (notify).
   */
  notifyCustomer?: boolean;
}

/** Hosting (hosting-admin-api), merged into `RegistrarApi` (one declaration line per area). */
export interface RegistrarApi extends HostingApi {}

export interface RegistrarApi extends CatalogApi, SubscriptionsApi, ModulesApi, AddonsApi, LookupApi, CountsApi, AccountsApi, NotifyApi, ZnsApi, EinvoiceApi, WorkApi, DeclarationsApi, WebhooksApi, ReportingApi, ServicesApi, NameChangeApi, CommerceApi, RegistryApi, MarketApi, CoreApi, GovernanceApi, AdminApi, IntegrationsApi, OperatorApi, DnsApi {
  // Session
  login(req: LoginRequest, opts?: CallOptions): Promise<LoginResponse>;
  /** `GET /auth/demo-login`: the OT&E quick sign-in's account; rejects (404) when it is off. */
  demoLoginStatus(opts?: CallOptions): Promise<DemoLogin>;
  /** `POST /auth/demo-login`: signs in as that account without password or code (OT&E only). */
  demoLogin(opts?: CallOptions): Promise<LoginResponse>;
  logout(opts?: WriteOptions): Promise<{ ok: boolean }>;
  me(opts?: CallOptions): Promise<Me>;
  /** `GET /deployment`: which server this is (OT&E or production); no session needed. */
  deployment(opts?: CallOptions): Promise<Deployment>;

  // Domains and registry
  listDomains(query: DomainListQuery, opts?: CallOptions): Promise<DomainListResponse>;
  getDomain(key: string, opts?: CallOptions): Promise<DomainDetail>;
  getDomainTimeline(key: string, query: TimelineQuery, opts?: CallOptions): Promise<DomainTimeline>;
  listDomainRegistryTransactions(key: string, opts?: CallOptions): Promise<ItemList<RegistryTransaction>>;
  listDomainComplianceDecisions(key: string, opts?: CallOptions): Promise<ItemList<PolicyDecision>>;
  getRegistryTransaction(id: string, opts?: CallOptions): Promise<RegistryTransactionDetail>;
  search(query: string, opts?: CallOptions): Promise<SearchResponse>;

  // Subjects
  getSubject(id: string, opts?: CallOptions): Promise<SubjectDetail>;
  revealSubjectIdentifier(identifierId: string, reason: string, opts?: WriteOptions): Promise<RevealResponse>;
  /** `POST /subjects/{id}/birth-date/reveal`: the current version's birth date (`YYYY-MM-DD`), audited. */
  revealSubjectBirthDate(subjectId: string, reason: string, opts?: WriteOptions): Promise<RevealResponse>;

  // Approvals and audit
  /** The live queue: pending, not yet expired. Used by the pages that request an approval. */
  listApprovals(opts?: CallOptions): Promise<ItemList<ApprovalRequest>>;
  /** The same queue with filters and paging, for the Administration > Approvals page. */
  listApprovalQueue(query: ApprovalQueueQuery, opts?: CallOptions): Promise<ApprovalPage>;
  createApproval(req: NewApproval, opts?: WriteOptions): Promise<ApprovalStatusChange>;
  decideApproval(id: string, req: ApprovalDecisionRequest, opts?: WriteOptions): Promise<ApprovalStatusChange>;
  listAudit(query: AuditQuery, opts?: CallOptions): Promise<AuditPage>;

  // Abuse
  listAbuseCases(filter: AbuseCaseFilter, opts?: CallOptions): Promise<ItemList<AbuseCase>>;
  getAbuseCase(id: string, opts?: CallOptions): Promise<AbuseCaseDetail>;
  createAbuseCase(req: NewAbuseReport, opts?: WriteOptions): Promise<OpenedAbuseCase>;
  transitionAbuseCase(id: string, req: AbuseTransitionRequest, opts?: WriteOptions): Promise<AbuseCase>;
  addAbuseEvidence(caseId: string, req: NewEvidence, opts?: WriteOptions): Promise<{ id: string; case_id: string }>;
  addLawEnforcementRequest(caseId: string, req: NewLawEnforcementRequest, opts?: WriteOptions): Promise<{ id: string; case_id: string; status: 'RECEIVED' }>;
  updateLawEnforcementRequest(id: string, req: LawEnforcementUpdate, opts?: WriteOptions): Promise<{ id: string; status: LawEnforcementStatus }>;
  requestDomainAction(caseId: string, req: DomainActionRequest, opts?: WriteOptions): Promise<DomainAction>;

  // Disputes
  listDisputes(filter: DisputeFilter, opts?: CallOptions): Promise<ItemList<Dispute>>;
  getDispute(id: string, opts?: CallOptions): Promise<Dispute>;
  openDispute(req: NewDispute, opts?: WriteOptions): Promise<Dispute>;
  decideDispute(id: string, req: DisputeDecisionRequest, opts?: WriteOptions): Promise<Dispute>;
  markDisputeImplemented(id: string, note: string, opts?: WriteOptions): Promise<Dispute>;
  closeDispute(id: string, req: DisputeCloseRequest, opts?: WriteOptions): Promise<Dispute>;

  // Disclosure
  listDisclosureCases(query: DisclosureListQuery, opts?: CallOptions): Promise<ItemList<DisclosureCaseSummary>>;
  getDisclosureCase(id: string, opts?: CallOptions): Promise<DisclosureCase>;
  createDisclosureCase(req: NewDisclosureRequest, opts?: WriteOptions): Promise<OpenedDisclosureCase>;
  startDisclosureReview(id: string, opts?: WriteOptions): Promise<DisclosureStatusChange>;
  requestDisclosureInfo(id: string, request: string, opts?: WriteOptions): Promise<DisclosureStatusChange>;
  decideDisclosureCase(id: string, req: DisclosureDecisionRequest, opts?: WriteOptions): Promise<DisclosureStatusChange>;
  releaseDisclosureCase(id: string, channel: string, opts?: WriteOptions): Promise<DisclosureReleaseResult>;
  closeDisclosureCase(id: string, reason: string, opts?: WriteOptions): Promise<DisclosureStatusChange>;

  // Observability API
  getOpsOverview(opts?: CallOptions): Promise<OpsOverview>;
  listOpsServices(opts?: CallOptions): Promise<ItemList<OpsService>>;
  listOpsRegistries(opts?: CallOptions): Promise<ItemList<OpsRegistry>>;
  getOpsQueues(opts?: CallOptions): Promise<OpsQueues>;
  listAlerts(opts?: CallOptions): Promise<ItemList<AlertmanagerAlert>>;
  queryMetrics(req: MetricsInstantRequest, opts?: CallOptions): Promise<PromQueryData>;
  queryMetricsRange(req: MetricsRangeRequest, opts?: CallOptions): Promise<PromQueryData>;
  queryLogs(req: LogsQueryRequest, opts?: CallOptions): Promise<LogsResponse>;
  tailLogs(query: string, sinceSeconds: number, opts?: CallOptions): Promise<LogsResponse>;
  getTrace(traceId: string, opts?: CallOptions): Promise<TempoTrace>;
  searchTraces(req: TraceSearchRequest, opts?: CallOptions): Promise<TempoSearchResponse>;
  correlate(correlationId: string, opts?: CallOptions): Promise<Correlation>;
  /** The logged exchanges of one registry command (needs `registry.payload.raw.view`; audited). */
  getRegistryExchanges(cltrid: string, opts?: CallOptions): Promise<RegistryExchanges>;
  getDomainLinks(name: string, opts?: CallOptions): Promise<DomainLinks>;
  listIncidents(opts?: CallOptions): Promise<ItemList<Incident>>;
  getIncident(id: string, opts?: CallOptions): Promise<IncidentDetail>;
  openIncident(req: NewIncident, opts?: WriteOptions): Promise<Incident>;
  transitionIncident(id: string, req: IncidentTransitionRequest, opts?: WriteOptions): Promise<Incident>;
  listSlos(opts?: CallOptions): Promise<ItemList<Slo>>;
  getLogRetention(opts?: CallOptions): Promise<LogRetention>;
  updateLogRetention(req: LogRetentionUpdate, opts?: WriteOptions): Promise<LogRetention>;

  // VNNIC compliance
  getVnnicCoverage(opts?: CallOptions): Promise<VnnicCoverage>;
  listVnProvinces(opts?: CallOptions): Promise<ItemList<VnProvince>>;
  listVnWards(provinceId: number, opts?: CallOptions): Promise<ItemList<VnWard>>;
  getVnPlacesStatus(opts?: CallOptions): Promise<VnPlacesStatus>;
  syncVnPlaces(opts?: CallOptions): Promise<{ sync_id: string; provinces: number; wards: number; retired: number }>;
  lookupVnnicEnterprise(gdtCode: string, opts?: CallOptions): Promise<VnnicBusinessLookup>;
  lookupVnnicHousehold(taxCode: string, opts?: CallOptions): Promise<VnnicBusinessLookup>;
  /** Looks the organisation of a PENDING verification up in the business registry and attaches the answer as evidence. */
  lookupVerificationRegistry(verificationId: string, opts?: WriteOptions): Promise<VerificationRegistryLookup>;
  listVnnicExceptions(query: VnnicExceptionQuery, opts?: CallOptions): Promise<ItemList<VnnicException>>;
  getVnnicRegistryList(query: VnnicRegistryListQuery, opts?: CallOptions): Promise<VnnicRegistryList>;
  /** "Đối chiếu ngay": reads VNNIC's domain list now and records the comparison. */
  checkVnnicRegistryList(opts?: WriteOptions): Promise<{ status: VnnicRegistryListCheck['status']; check: VnnicRegistryListCheck }>;
  listVnnicSync(query: VnnicSyncQuery, opts?: CallOptions): Promise<ItemList<VnnicSyncRow>>;
  getVnnicSyncTransaction(id: string, opts?: CallOptions): Promise<VnnicSyncTransaction>;
  recordVnnicSyncOutcome(
    id: string,
    req: VnnicSyncOutcomeRequest,
    opts?: WriteOptions,
  ): Promise<VnnicStatusChange<VnnicTransactionStatus>>;
  resubmitVnnicSync(dossierId: string, reason: string, opts?: WriteOptions): Promise<VnnicResubmitResponse>;
  listVnnicReports(query: VnnicReportQuery, opts?: CallOptions): Promise<ItemList<VnnicReportBatch>>;
  getVnnicReport(id: string, opts?: CallOptions): Promise<VnnicReportBatchDetail>;
  generateVnnicReport(req: VnnicGenerateReportRequest, opts?: WriteOptions): Promise<VnnicGenerateReportResponse>;
  exportVnnicReport(id: string, opts?: WriteOptions): Promise<VnnicExportResponse>;
  recordVnnicReportOutcome(
    id: string,
    req: VnnicReportOutcomeRequest,
    opts?: WriteOptions,
  ): Promise<VnnicStatusChange<VnnicReportStatus>>;
}
